// Assessments route (/$region/assessments, Confidential): the tab (Assessments or Capability documents) and the status filter in the URL; the screen is AssessmentsPage.
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { enumWithDefault, optionalList } from "@/app/search";
import { RestrictedRoute } from "@/app/shell/RouteStates";
import {
  ASSESSMENTS_TABS,
  AssessmentsPage,
  type AssessmentsSearch,
} from "@/features/assessments/AssessmentsPage";
import { ASSESSMENT_STAGES } from "@/features/assessments/labels";

const searchSchema = z.object({
  tab: enumWithDefault(ASSESSMENTS_TABS, "assessments"),
  status: optionalList(z.enum(ASSESSMENT_STAGES)),
});

const searchDefaults = { tab: "assessments" } satisfies Partial<z.output<typeof searchSchema>>;

export const Route = createFileRoute("/_app/$region/assessments/")({
  validateSearch: searchSchema,
  search: { middlewares: [stripSearchParams(searchDefaults)] },
  staticData: { crumb: "Assessments" },
  component: AssessmentsRoute,
});

function AssessmentsRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const onSearchChange = useCallback(
    (patch: Partial<AssessmentsSearch>, replace = false) => {
      void navigate({ search: (previous) => ({ ...previous, ...patch }), replace });
    },
    [navigate],
  );
  return (
    <RestrictedRoute level="C" description="Compliance assessments are for users with the Confidential role.">
      <AssessmentsPage search={search} onSearchChange={onSearchChange} />
    </RestrictedRoute>
  );
}
